import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Send, Save, Trash2, ArrowLeft, Mail, AlertTriangle, RefreshCw, Plus } from "lucide-react";

const NONE = "__none__";
const EMPTY = { business_name: "", email: "", website: "", service_category: "", context: "", subject: "", body: "" };

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-kid-mist text-kid-muted" },
  sent: { label: "Sent", cls: "bg-kid-sky/20 text-[#2b7fa8]" },
  opened: { label: "Opened", cls: "bg-kid-sun/20 text-[#8a6100]" },
  registered: { label: "Registered", cls: "bg-kid-green/15 text-[#3f9d5c]" },
};

export default function AdminProviderInvites() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [dup, setDup] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => { api.get("/marketplace/meta").then((r) => setCats(r.data.categories || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/marketplace/invites"); setInvites(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const checkDup = useCallback(async (email) => {
    if (!email || !email.includes("@")) { setDup(null); return; }
    try {
      const { data } = await api.get("/admin/marketplace/invites/check", { params: { email } });
      setDup(data.invited && data.invite?.id !== editingId ? data.invite : null);
    } catch { setDup(null); }
  }, [editingId]);

  const generate = async () => {
    if (!form.business_name.trim()) { toast.error("Add the provider's business name first."); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/marketplace/invites/generate", {
        business_name: form.business_name, email: form.email || null, website: form.website || null,
        service_category: form.service_category || null, context: form.context || null,
      });
      setForm((f) => ({ ...f, subject: data.subject, body: data.body }));
      toast.success("Draft email generated — review and edit before sending.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const persist = async () => {
    const payload = {
      business_name: form.business_name.trim(), email: form.email.trim(), website: form.website || null,
      service_category: form.service_category || null, context: form.context || null,
      subject: form.subject.trim(), body: form.body.trim(),
    };
    if (editingId) { await api.patch(`/admin/marketplace/invites/${editingId}`, payload); return editingId; }
    const { data } = await api.post("/admin/marketplace/invites", payload);
    setEditingId(data.id);
    return data.id;
  };

  const validate = () => {
    if (!form.business_name.trim() || !form.email.trim()) { toast.error("Business name and email are required."); return false; }
    if (!form.subject.trim() || !form.body.trim()) { toast.error("Generate or write the subject and body first."); return false; }
    return true;
  };

  const saveDraft = async () => {
    if (!validate()) return;
    setSaving(true);
    try { await persist(); toast.success("Draft saved."); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendNow = async () => {
    if (!validate()) return;
    if (dup) { if (!window.confirm(`You've already invited ${form.email}. Send again anyway?`)) return; }
    setSending(true);
    try {
      const id = await persist();
      await api.post(`/admin/marketplace/invites/${id}/send`);
      toast.success(`Invite sent to ${form.email}.`);
      resetForm(); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const resetForm = () => { setForm(EMPTY); setEditingId(null); setDup(null); };

  const editInvite = (inv) => {
    setEditingId(inv.id);
    setForm({
      business_name: inv.business_name || "", email: inv.email || "", website: inv.website || "",
      service_category: inv.service_category || "", context: inv.context || "",
      subject: inv.subject || "", body: inv.body || "",
    });
    setDup(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (inv) => {
    if (!window.confirm(`Delete the invite for ${inv.business_name}?`)) return;
    try { await api.delete(`/admin/marketplace/invites/${inv.id}`); toast.success("Invite deleted."); if (editingId === inv.id) resetForm(); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const catName = (slug) => cats.find((c) => c.slug === slug)?.name || slug;

  const funnel = (() => {
    const sent = invites.filter((i) => i.status !== "draft").length;
    const opened = invites.filter((i) => i.status === "opened" || i.status === "registered").length;
    const registered = invites.filter((i) => i.status === "registered").length;
    const openRate = sent ? Math.round((opened / sent) * 100) : 0;
    const convRate = sent ? Math.round((registered / sent) * 100) : 0;
    return { sent, opened, registered, openRate, convRate };
  })();

  return (
    <div data-testid="admin-provider-invites">
      <Link to="/ops/marketplace" className="inline-flex items-center gap-1.5 text-kid-teal font-semibold text-sm mb-3" data-testid="invites-back">
        <ArrowLeft size={16} /> Back to Service Finder
      </Link>
      <PageHeader eyebrow="Service Finder" title="Invite providers"
        subtitle="Draft an AI-written invitation, edit it, and email providers to join the KidPath directory." />

      {invites.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="invite-funnel">
          <div className="kid-card p-4" data-testid="funnel-sent">
            <div className="text-2xl font-bold text-kid-navy">{funnel.sent}</div>
            <div className="text-xs text-kid-muted mt-0.5">Sent</div>
          </div>
          <div className="kid-card p-4" data-testid="funnel-opened">
            <div className="text-2xl font-bold text-kid-navy">{funnel.opened}</div>
            <div className="text-xs text-kid-muted mt-0.5">Opened · {funnel.openRate}%</div>
          </div>
          <div className="kid-card p-4" data-testid="funnel-registered">
            <div className="text-2xl font-bold text-kid-navy">{funnel.registered}</div>
            <div className="text-xs text-kid-muted mt-0.5">Registered</div>
          </div>
          <div className="kid-card p-4 bg-kid-teal/5" data-testid="funnel-conversion">
            <div className="text-2xl font-bold text-kid-teal">{funnel.convRate}%</div>
            <div className="text-xs text-kid-muted mt-0.5">Conversion rate</div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Composer */}
        <div className="lg:col-span-3 space-y-4">
          <div className="kid-card p-5 space-y-4" data-testid="invite-composer">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Business name *</Label>
                <Input value={form.business_name} onChange={set("business_name")} placeholder="Bright Steps Speech Therapy" className="mt-1.5 bg-white" data-testid="invite-business-name" />
              </div>
              <div>
                <Label className="text-sm">Provider email *</Label>
                <Input type="email" value={form.email} onChange={set("email")} onBlur={(e) => checkDup(e.target.value)} placeholder="hello@provider.ca" className="mt-1.5 bg-white" data-testid="invite-email" />
              </div>
              <div>
                <Label className="text-sm">Website</Label>
                <Input value={form.website} onChange={set("website")} placeholder="https://…" className="mt-1.5 bg-white" data-testid="invite-website" />
              </div>
              <div>
                <Label className="text-sm">Service type</Label>
                <Select value={form.service_category || NONE} onValueChange={(v) => setForm((f) => ({ ...f, service_category: v === NONE ? "" : v }))}>
                  <SelectTrigger className="mt-1.5 bg-white" data-testid="invite-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value={NONE}>Not specified</SelectItem>{cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {dup && (
              <div className="flex items-start gap-2 text-sm bg-kid-sun/15 text-[#8a6100] rounded-xl px-3 py-2" data-testid="invite-dup-warning">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <span>This email was already invited on {new Date(dup.created_at).toLocaleDateString("en-CA")} (status: {dup.status}).</span>
              </div>
            )}

            <div>
              <Label className="text-sm">Context for the AI (optional)</Label>
              <Textarea value={form.context} onChange={set("context")} rows={3} data-testid="invite-context"
                placeholder="e.g. They specialize in AAC and gestalt language processing in Ottawa. Mention our founding-provider spotlight at launch." className="mt-1.5 bg-white" />
              <p className="text-xs text-kid-muted mt-1">Notes here are folded into the generated email to improve quality.</p>
            </div>

            <Button onClick={generate} disabled={generating} className="rounded-full" data-testid="invite-generate-btn">
              <Sparkles size={15} className="mr-1.5" /> {generating ? "Generating…" : "Generate email with AI"}
            </Button>
          </div>

          {(form.subject || form.body) && (
            <div className="kid-card p-5 space-y-4" data-testid="invite-editor">
              <div>
                <Label className="text-sm">Subject</Label>
                <Input value={form.subject} onChange={set("subject")} className="mt-1.5 bg-white" data-testid="invite-subject" />
              </div>
              <div>
                <Label className="text-sm">Email body</Label>
                <Textarea value={form.body} onChange={set("body")} rows={12} className="mt-1.5 bg-white font-mono text-sm" data-testid="invite-body" />
                <p className="text-xs text-kid-muted mt-1">A branded header, a "List your practice" button and signature are added automatically when sent.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={sendNow} disabled={sending} className="rounded-full" data-testid="invite-send-btn">
                  <Send size={15} className="mr-1.5" /> {sending ? "Sending…" : "Send invite"}
                </Button>
                <Button onClick={saveDraft} disabled={saving} variant="outline" className="rounded-full" data-testid="invite-save-btn">
                  <Save size={15} className="mr-1.5" /> {saving ? "Saving…" : "Save draft"}
                </Button>
                {editingId && (
                  <Button onClick={resetForm} variant="ghost" className="rounded-full" data-testid="invite-new-btn">
                    <Plus size={15} className="mr-1.5" /> New
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* History */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-bold text-kid-navy">Invite history</h2>
            <button onClick={load} className="text-kid-muted hover:text-kid-navy" data-testid="invite-refresh"><RefreshCw size={16} /></button>
          </div>
          {loading ? <Spinner /> : invites.length === 0 ? (
            <div className="kid-card p-8 text-center text-kid-muted text-sm" data-testid="invites-empty">
              <Mail size={26} className="mx-auto mb-2 opacity-60" /> No invites yet. Generate one to get started.
            </div>
          ) : (
            <div className="space-y-2" data-testid="invites-list">
              {invites.map((inv) => (
                <div key={inv.id} className="kid-card p-4" data-testid="invite-row">
                  <div className="flex items-start justify-between gap-3">
                    <button onClick={() => editInvite(inv)} className="text-left min-w-0 flex-1" data-testid="invite-open">
                      <div className="font-semibold text-kid-navy truncate">{inv.business_name}</div>
                      <div className="text-xs text-kid-muted truncate">{inv.email}</div>
                    </button>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${(STATUS_META[inv.status] || STATUS_META.draft).cls}`} data-testid="invite-status">{(STATUS_META[inv.status] || STATUS_META.draft).label}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-kid-muted">
                      {inv.service_category ? catName(inv.service_category) + " · " : ""}
                      {inv.status === "registered" && inv.registered_at ? `Joined ${new Date(inv.registered_at).toLocaleDateString("en-CA")}`
                        : inv.status === "opened" && inv.last_opened_at ? `Opened ${new Date(inv.last_opened_at).toLocaleDateString("en-CA")}${inv.open_count ? ` · ${inv.open_count}×` : ""}`
                        : inv.status === "sent" && inv.sent_at ? `Sent ${new Date(inv.sent_at).toLocaleDateString("en-CA")}`
                        : `Draft ${new Date(inv.created_at).toLocaleDateString("en-CA")}`}
                    </span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => editInvite(inv)} className="text-kid-teal text-xs font-semibold px-2 py-1" data-testid="invite-edit">{inv.status === "draft" ? "Edit" : "View / resend"}</button>
                      <button onClick={() => del(inv)} className="text-kid-muted hover:text-kid-coral p-1" data-testid="invite-delete"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
